/**
 * usePlatformSettings — shared React hook that wraps the platform-settings
 * SDK call in a long-cached React Query. Every page that gates a new feature
 * on a flag uses this hook so the network call hits once per session, not
 * once per feature.
 *
 * Returns a guaranteed-non-null settings object: while the network call is
 * in flight (or has failed) we fall back to a conservative default that
 * keeps the existing UI working but treats new features as off.
 */

import { useQuery } from '@tanstack/react-query';
import { useBookingPlatformSettingsService } from '@actschurch/ccms-sdk';
import type { BookingPlatformSettings } from '@actschurch/ccms-sdk';

const DEFAULT_SETTINGS: BookingPlatformSettings = {
  settingsID: 1,
  enableGoogleLocation: false,
  enableRfidScan: true,
  enableInventoryAudit: true,
  enableLiveTracking: false,
  enableRecurringBookings: true,
  enableWaitlist: true,
  enableDriverProfileCheck: true,
  enableExpiryAlerts: true,
  notifyEmail: true,
  notifySms: false,
  notifyWhatsApp: false,
  notifyPush: true,
  createdDateTime: 0,
  modifiedDateTime: 0,
};

export function usePlatformSettings(): BookingPlatformSettings {
  const service = useBookingPlatformSettingsService();
  const { data } = useQuery<BookingPlatformSettings | null>({
    queryKey: ['booking-platform-settings'],
    queryFn: () => service.get(),
    staleTime: 5 * 60 * 1000,    // 5 min — settings change very rarely
    refetchOnWindowFocus: false,
  });
  return data ?? DEFAULT_SETTINGS;
}

/** Returns true when the named channel is active in platform settings. */
export function useChannelEnabled(channel: 'email' | 'sms' | 'whatsapp' | 'push'): boolean {
  const s = usePlatformSettings();
  switch (channel) {
    case 'email':    return s.notifyEmail;
    case 'sms':      return s.notifySms;
    case 'whatsapp': return s.notifyWhatsApp;
    case 'push':     return s.notifyPush;
  }
}
